import React from 'react';
import {toast} from 'react-toastify';
import {useTranslation} from 'react-i18next';
import {usePdfParser} from '../../../hooks/parsers/usePdfParser';
import {useTextParser} from '../../../hooks/parsers/useTextParser';

export interface ParsedFile {
    content: string;
    pages?: string[];
}

export interface UseFileTransferResult {
    isParsing: boolean;
    isPdfParsing: boolean;
    pdfProgress: number;
    pdfPages: string[];
    pickAndParse: () => Promise<ParsedFile | undefined>;
    download: (filename: string, content: string) => void;
    clearPdfPages: () => void;
}

const ACCEPTED_EXTENSIONS = ['.md', '.pdf'] as const;
const ACCEPTED_FILE_TYPES = ACCEPTED_EXTENSIONS.join(',');

export function useFileTransfer(): UseFileTransferResult {
    const {t} = useTranslation();
    const {parse: parsePdf, loading: isPdfParsing, progress: pdfProgress} = usePdfParser();
    const {parse: parseText, loading: isTextParsing} = useTextParser();

    const [pdfPages, setPdfPages] = React.useState<string[]>([]);

    const pickAndParse = (): Promise<ParsedFile | undefined> =>
        new Promise((resolve) => {
            const input = document.createElement('input');
            input.type = 'file';
            input.accept = ACCEPTED_FILE_TYPES;
            input.oncancel = () => resolve(undefined);
            input.onchange = (e: Event) => {
                const target = e.target as HTMLInputElement;
                const file = target?.files?.[0];
                if (!file) {
                    resolve(undefined);
                    return;
                }

                const name = file.name.toLowerCase();
                if (!ACCEPTED_EXTENSIONS.some((ext) => name.endsWith(ext))) {
                    toast.error(t('generator.invalidFileType', {types: ACCEPTED_FILE_TYPES}));
                    resolve(undefined);
                    return;
                }

                const isPdf = name.endsWith('.pdf');
                const parse = isPdf ? parsePdf : parseText;
                parse(file).then((result) => {
                    const pages = isPdf && Array.isArray(result) ? result : undefined;
                    const content = Array.isArray(result) ? result.join('\n') : result;
                    if (pages) setPdfPages(pages);
                    resolve({content, pages});
                });
            };
            input.click();
        });

    const download = (filename: string, content: string): void => {
        const element = document.createElement('a');
        const file = new Blob([content], {type: 'text/markdown'});
        element.href = URL.createObjectURL(file);
        element.download = filename;
        document.body.appendChild(element);
        element.click();
    };

    const clearPdfPages = (): void => setPdfPages([]);

    return {
        isParsing: isPdfParsing || isTextParsing,
        isPdfParsing,
        pdfProgress,
        pdfPages,
        pickAndParse,
        download,
        clearPdfPages,
    };
}
